import React, { useRef } from "react";
import dayjs from "dayjs";
import {
  FaUser,
  FaHome,
  FaCalendarAlt,
  FaIdCard,
  FaGlobe,
  FaSignature,
  FaFilePdf,
} from "react-icons/fa";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const MarriageView = ({ currentRecord }) => {
  const contentRef = useRef(null);

  const generatePDF = async () => {
    const content = contentRef.current;

    // Pre-load all images
    const images = content.getElementsByTagName("img");
    await Promise.all(
      Array.from(images).map((img) => {
        if (img.complete) return Promise.resolve();
        return new Promise((resolve) => {
          img.onload = resolve;
          img.onerror = resolve;
        });
      })
    );

    const canvas = await html2canvas(content, {
      scale: 1.5,
      useCORS: true,
      allowTaint: true,
      logging: false,
      windowWidth: 800, // Reduced width for better scaling
      windowHeight: content.scrollHeight,
      onclone: (clonedDoc) => {
        const clonedContent = clonedDoc.querySelector("[data-pdf-content]");
        if (clonedContent) {
          clonedContent.style.width = "800px";
          clonedContent.style.margin = "0 auto";
          clonedContent.style.padding = "20px";

          // Center all sections
          const sections = clonedContent.querySelectorAll(
            ".bg-white.rounded-lg"
          );
          sections.forEach((section) => {
            section.style.maxWidth = "100%";
            section.style.margin = "0 auto 2rem auto";
            section.style.pageBreakInside = "avoid";
            section.style.padding = "15px";
          });

          // Adjust grid layouts
          const grids = clonedContent.querySelectorAll(".grid");
          grids.forEach((grid) => {
            grid.style.display = "grid";
            grid.style.gridTemplateColumns = "1fr 1fr";
            grid.style.gap = "1rem";
            grid.style.width = "100%";
          });

          // Adjust field displays
          const fields = clonedContent.querySelectorAll(".flex.flex-col");
          fields.forEach((field) => {
            field.style.marginBottom = "0.5rem";
          });

          // Adjust signature images
          const signatureContainers = clonedContent.querySelectorAll(
            ".signature-container"
          );
          signatureContainers.forEach((container) => {
            container.style.pageBreakInside = "avoid";
            container.style.marginBottom = "1rem";
          });

          const signatureImages = clonedContent.getElementsByTagName("img");
          Array.from(signatureImages).forEach((img) => {
            const container = img.parentElement;
            container.style.height = "80px";
            container.style.display = "flex";
            container.style.alignItems = "center";
            container.style.justifyContent = "center";
            img.style.maxHeight = "100%";
            img.style.width = "auto";
            img.style.maxWidth = "200px";
            img.style.objectFit = "contain";
          });
        }
      },
    });

    // Calculate dimensions
    const imgWidth = 210 - 40; // A4 width minus margins
    const pageHeight = 297 - 40; // A4 height minus margins
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    // Create PDF with margins
    const doc = new jsPDF("p", "mm", "a4");
    let position = 20; // Top margin
    let heightLeft = imgHeight;

    // Add first page
    doc.addImage(
      canvas.toDataURL("image/jpeg", 1.0),
      "JPEG",
      20, // Left margin
      position,
      imgWidth,
      imgHeight,
      "",
      "FAST"
    );
    heightLeft -= pageHeight;

    // Add additional pages if needed
    while (heightLeft >= 0) {
      position = heightLeft - imgHeight + 20; // Add top margin for each page
      doc.addPage();
      doc.addImage(
        canvas.toDataURL("image/jpeg", 1.0),
        "JPEG",
        20, // Left margin
        position,
        imgWidth,
        imgHeight,
        "",
        "FAST"
      );
      heightLeft -= pageHeight;
    }

    doc.save(`Marriage_Record_${currentRecord.record_no}.pdf`);
  };

  const formatDate = (date) => {
    return date ? dayjs(date).utc().format("MMMM D, YYYY") : "Not Provided";
  };

  const renderSignature = (signature, label) => (
    <div className="flex flex-col items-center w-full signature-container">
      <span className="text-sm font-medium text-gray-500">{label}</span>
      {signature ? (
        <div className="flex justify-center items-center mt-1 w-full h-20">
          <img
            src={`${process.env.REACT_APP_REPORTS_DOWNLOAD_URL}uploads/marriage/${signature}`}
            alt={`${label} signature`}
            className="object-contain w-auto max-h-full"
            crossOrigin="anonymous"
            style={{ maxWidth: "200px" }}
          />
        </div>
      ) : (
        <span className="mt-1 italic text-gray-500">No signature provided</span>
      )}
    </div>
  );

  const renderSection = (title, icon, content) => (
    <div className="p-4 mb-6 bg-white rounded-lg shadow section-container">
      <div className="flex justify-center items-center mb-4">
        {icon}
        <h2 className="ml-2 text-xl font-semibold text-gray-800">{title}</h2>
      </div>
      <div className="grid grid-cols-2 gap-6">{content}</div>
    </div>
  );

  const renderField = (label, value) => (
    <div className="flex flex-col items-start mb-2">
      <span className="text-sm font-medium text-gray-500">{label}</span>
      <span className="mt-1 text-gray-900">{value || "Not Provided"}</span>
    </div>
  );

  const renderLocation = (country, state, city, address) => (
    <>
      {renderField("Country", country?.label)}
      {renderField("State", state?.label)}
      {renderField("City", city?.label)}
      {renderField("Address", address)}
    </>
  );

  return (
    <div className="p-6 mx-auto max-w-5xl">
      {/* Download PDF Button */}
      <div className="flex justify-end mb-4">
        <button
          onClick={generatePDF}
          className="flex items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          <FaFilePdf className="mr-2" />
          Download PDF
        </button>
      </div>

      {/* Content to be converted to PDF */}
      <div ref={contentRef} data-pdf-content className="p-8 bg-white">
        {/* Basic Information */}
        <div className="mb-8 text-center">
          <h1 className="mb-2 text-3xl font-bold text-gray-900">
            Marriage Record
          </h1>
          <div className="text-gray-600">
            <span className="font-semibold">Record No: </span>
            {currentRecord.record_no}
          </div>
          <div className="text-gray-600">
            <span className="font-semibold">Marriage Date: </span>
            {formatDate(currentRecord.marriage_date)}
          </div>
        </div>

        {/* Groom Details */}
        {renderSection(
          "Groom Details",
          <FaUser className="w-6 h-6 text-blue-600" />,
          <>
            {renderField("Christian Name", currentRecord.groom_christian_name)}
            {renderField("Surname", currentRecord.groom_surname)}
            {renderField(
              "Date of Birth",
              formatDate(currentRecord.groom_date_of_birth)
            )}
            {renderField("Caste", currentRecord.groom_caste?.label)}
            {renderField("Status", currentRecord.groom_status?.label)}
            {renderField(
              "By Banns/License",
              currentRecord.groom_by_banns_or_license?.label
            )}
            {renderField(
              "Parents/Guardian",
              currentRecord.groom_parents_or_guardian
            )}
          </>
        )}

        {/* Groom Location */}
        {renderSection(
          "Groom Location",
          <FaGlobe className="w-6 h-6 text-green-600" />,
          renderLocation(
            currentRecord.groom_country,
            currentRecord.groom_state,
            currentRecord.groom_city,
            currentRecord.groom_residential_address
          )
        )}

        {/* Groom Signatures */}
        {renderSection(
          "Groom Signatures",
          <FaSignature className="w-6 h-6 text-blue-600" />,
          <>
            {renderSignature(
              currentRecord.groom_signature,
              "Groom's Signature"
            )}
            {renderSignature(
              currentRecord.groom_signature_witness,
              "Witness Signature"
            )}
          </>
        )}

        {/* Bride Details */}
        {renderSection(
          "Bride Details",
          <FaUser className="w-6 h-6 text-pink-600" />,
          <>
            {renderField("Christian Name", currentRecord.bride_christian_name)}
            {renderField("Surname", currentRecord.bride_surname)}
            {renderField(
              "Date of Birth",
              formatDate(currentRecord.bride_date_of_birth)
            )}
            {renderField("Caste", currentRecord.bride_caste?.label)}
            {renderField("Status", currentRecord.bride_status?.label)}
            {renderField(
              "By Banns/License",
              currentRecord.bride_by_banns_or_license?.label
            )}
            {renderField(
              "Parents/Guardian",
              currentRecord.bride_parents_or_guardian
            )}
          </>
        )}

        {/* Bride Location */}
        {renderSection(
          "Bride Location",
          <FaGlobe className="w-6 h-6 text-purple-600" />,
          renderLocation(
            currentRecord.bride_country,
            currentRecord.bride_state,
            currentRecord.bride_city,
            currentRecord.bride_residential_address
          )
        )}

        {/* Bride Signatures */}
        {renderSection(
          "Bride Signatures",
          <FaSignature className="w-6 h-6 text-pink-600" />,
          <>
            {renderSignature(
              currentRecord.bride_signature,
              "Bride's Signature"
            )}
            {renderSignature(
              currentRecord.bride_signature_witness,
              "Witness Signature"
            )}
          </>
        )}

        {/* Marriage Details */}
        {renderSection(
          "Marriage Details",
          <FaCalendarAlt className="w-6 h-6 text-indigo-600" />,
          <>
            {renderField("Marriage Location", currentRecord.marriage_location)}
            {renderField("Solemnised By", currentRecord.solemnised_by)}
            {renderField("Created At", formatDate(currentRecord.createdAt))}
            {renderField("Last Updated", formatDate(currentRecord.updatedAt))}
          </>
        )}
      </div>
    </div>
  );
};

export default MarriageView;
